import type {
  WhatsAppTemplate,
  WhatsAppTemplateBodyComponent,
  WhatsAppTemplateFooterComponent,
  WhatsAppTemplateHeaderComponent,
  WhatsAppTemplateMessageBodyComponent,
  WhatsAppTemplateMessageComponent,
  WhatsAppTemplateMessageHeaderComponent,
  WhatsAppTemplateMessageTextParameter,
} from 'src/api/generated';

export const ESCAPED_PLACEHOLDER_PREFIX = '\\{\\{';
export const ESCAPED_PLACEHOLDER_SUFFIX = '\\}\\}';

/**
 * Replaces numbered placeholders in a text with corresponding values
 * @param text - The template text containing placeholders like {{1}}, {{2}}, etc.
 * @param values - Array of values to replace the placeholders with
 * @param replacer - Optional string to wrap around replaced values
 * @returns The text with placeholders replaced by values
 */
export const replacePlaceholders = (
  text: string,
  values: string[],
  replacer = ''
): string => {
  return text.replace(/\{\{(\d+)\}\}/g, (token: string, position: string) => {
    const value = values[Number(position) - 1];
    return value ? `${replacer}${value}${replacer}` : token;
  });
};

/**
 * Builds the text content for a template component by replacing placeholders with parameter values
 * @param templateComponent - The template component containing the text template
 * @param messageComponent - Optional message component containing parameter values
 * @param replacer - Optional string to wrap around replaced values
 * @returns The component text with parameters replaced
 */
export const buildComponentText = (
  templateComponent:
    | WhatsAppTemplateHeaderComponent
    | WhatsAppTemplateBodyComponent
    | WhatsAppTemplateFooterComponent,
  messageComponent?:
    | WhatsAppTemplateMessageHeaderComponent
    | WhatsAppTemplateMessageBodyComponent,
  replacer = ''
): string => {
  const componentText = templateComponent.text ?? '';

  const componentParameters = messageComponent?.parameters ?? [];
  const textParameters = componentParameters.filter(
    (param): param is WhatsAppTemplateMessageTextParameter =>
      param.type.toLowerCase() === 'text'
  );
  const parameterValues = textParameters.map(
    (value: WhatsAppTemplateMessageTextParameter) => value.text
  );

  const replacedText = replacePlaceholders(
    componentText,
    parameterValues,
    replacer
  );
  return replacedText;
};

/**
 * Builds the header text for a WhatsApp template message
 * @param template - The WhatsApp template
 * @param messageComponents - Array of message components containing parameter values
 * @param replacer - Optional string to wrap around replaced values
 * @returns The header text with parameters replaced
 */
export const buildHeaderText = (
  template: WhatsAppTemplate,
  messageComponents: WhatsAppTemplateMessageComponent[],
  replacer = ''
): string => {
  const templateHeaderComponent = template.components.find(
    (comp) => comp.type.toLowerCase() === 'header'
  ) as WhatsAppTemplateHeaderComponent | undefined;
  if (!templateHeaderComponent) return '';

  const messageHeaderComponent = messageComponents.find(
    (comp) => comp.type.toLowerCase() === 'header'
  ) as WhatsAppTemplateMessageHeaderComponent | undefined;

  return buildComponentText(
    templateHeaderComponent,
    messageHeaderComponent,
    replacer
  );
};

/**
 * Builds the body text for a WhatsApp template message
 * @param template - The WhatsApp template
 * @param messageComponents - Array of message components containing parameter values
 * @param replacer - Optional string to wrap around replaced values
 * @returns The body text with parameters replaced
 */
export const buildBodyText = (
  template: WhatsAppTemplate,
  messageComponents: WhatsAppTemplateMessageComponent[],
  replacer = ''
): string => {
  const templateBodyComponent = template.components.find(
    (comp) => comp.type.toLowerCase() === 'body'
  ) as WhatsAppTemplateBodyComponent | undefined;
  if (!templateBodyComponent) return '';

  const messageBodyComponent = messageComponents.find(
    (comp) => comp.type.toLowerCase() === 'body'
  ) as WhatsAppTemplateMessageBodyComponent | undefined;

  return buildComponentText(
    templateBodyComponent,
    messageBodyComponent,
    replacer
  );
};

/**
 * Builds the footer text for a WhatsApp template message
 * @param template - The WhatsApp template
 * @returns The footer text
 */
export const buildFooterText = (template: WhatsAppTemplate): string => {
  const templateFooterComponent = template.components.find(
    (comp) => comp.type.toLowerCase() === 'footer'
  ) as WhatsAppTemplateFooterComponent | undefined;
  if (!templateFooterComponent) return '';

  return templateFooterComponent.text;
};

/**
 * Builds a complete template message using component-based parameters
 * @param template - The WhatsApp template
 * @param messageComponents - Array of message components containing parameter values
 * @param replacer - Optional string to wrap around replaced values
 * @returns The complete message with all components assembled and parameters replaced
 */
export const buildTemplateMessageFromComponents = (
  template: WhatsAppTemplate,
  messageComponents: WhatsAppTemplateMessageComponent[] = [],
  replacer = ''
): string => {
  const headerText = buildHeaderText(template, messageComponents, replacer);
  const bodyText = buildBodyText(template, messageComponents, replacer);
  const footerText = buildFooterText(template);

  return [headerText, bodyText, footerText].filter(Boolean).join('\n');
};
